import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getBlogDetailStats, deleteComment } from "../../api/blog";
import { Card, CardHeader, CardTitle, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import Loader from "../../components/ui/Loader";
import { ArrowLeft, Heart, MessageCircle, Calendar, User, Trash2, Mail, ExternalLink, PenTool } from "lucide-react";
import { format } from "date-fns";
import { toast } from "react-hot-toast";
import ConfirmDialog from "../../components/common/ConfirmDialog";

export default function AdminBlogDetailStats() {
 const { id } = useParams();
 const navigate = useNavigate();
 const [blog, setBlog] = useState(null);
 const [loading, setLoading] = useState(true);

 useEffect(() => {
 fetchData();
 }, [id]);

 const fetchData = async () => {
 try {
 const data = await getBlogDetailStats(id);
 setBlog(data);
 } catch (error) {
 console.error("Failed to fetch detail stats", error);
 toast.error("Analytics not found");
 navigate("/admin/blog-analytics");
 } finally {
 setLoading(false);
 }
 };

 const handleDelete = async (commentId) => {
 try {
 await deleteComment(commentId);
 setBlog(prev => ({
 ...prev,
 comments: prev.comments.filter(c => c.id !== commentId),
 _count: { ...prev._count, comments: prev._count.comments - 1 }
 }));
 toast.success("Comment deleted");
 } catch (error) {
 toast.error("Failed to delete");
 }
 };

 if (loading) return <Loader />;
 if (!blog) return null;

 return (
 <div className="min-h-screen py-6 md:py-10">
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
 {/* Header */}
 <div className="mb-8">
 <Button 
 variant="outline" 
 onClick={() => navigate("/admin/blog-analytics")}
 className="mb-6"
 >
 <ArrowLeft className="w-4 h-4 mr-2" />
 Back to Analytics
 </Button>
 
 <div className="flex items-center gap-3 mb-2">
 <div className="p-2.5 rounded-xl bg-gradient-to-r from-primary to-accent shadow-lg">
 <PenTool className="w-6 h-6 text-white" />
 </div>
 <h1 className="text-3xl md:text-4xl font-bold">Blog Detail Stats</h1>
 </div>
 <p className="text-muted-foreground">Detailed analytics for this blog post</p>
 </div>

 <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
 {/* Blog Info */}
 <div className="lg:col-span-1 space-y-6">
 <Card className="overflow-hidden">
 <div className="aspect-video w-full ">
 {blog.coverImage ? (
 <img src={blog.coverImage} className="w-full h-full object-cover" alt={blog.title} />
 ) : (
 <div className="w-full h-full flex items-center justify-center">
 <PenTool className="w-16 h-16 text-muted-foreground" />
 </div>
 )}
 </div>
 <CardContent className="p-6">
 <h2 className="text-xl font-bold mb-4 line-clamp-2">{blog.title}</h2>
 <div className="space-y-3 text-sm">
 <div className="flex items-center gap-2 text-muted-foreground">
 <Calendar className="w-4 h-4" /> 
 {format(new Date(blog.createdAt), "MMMM d, yyyy")}
 </div>
 <div className="flex items-center gap-2 text-muted-foreground">
 <User className="w-4 h-4" /> 
 by {blog.author?.email}
 </div>
 </div>
 <Button 
 variant="outline" 
 className="w-full mt-6"
 onClick={() => window.open(`/blog/${blog.id}`, "_blank")}
 >
 <ExternalLink className="w-4 h-4 mr-2" /> 
 View Public Post
 </Button>
 </CardContent>
 </Card>

 {/* Stats Cards */}
 <div className="grid grid-cols-2 gap-4">
 <Card>
 <CardContent className="p-5 text-center">
 <Heart className="w-6 h-6 text-rose-500 mx-auto mb-2" />
 <div className="text-3xl font-bold">{blog._count.likes}</div>
 <div className="text-sm text-muted-foreground">Likes</div>
 </CardContent>
 </Card>
 <Card>
 <CardContent className="p-5 text-center">
 <MessageCircle className="w-6 h-6 text-blue-500 mx-auto mb-2" />
 <div className="text-3xl font-bold">{blog._count.comments}</div>
 <div className="text-sm text-muted-foreground">Comments</div>
 </CardContent>
 </Card>
 </div>
 </div>

 {/* Engagement Details */}
 <div className="lg:col-span-2 space-y-6">
 {/* Likers */}
 <Card>
 <CardHeader>
 <CardTitle className="flex items-center gap-2">
 <Heart className="w-5 h-5 text-rose-500" /> 
 Users who Liked
 </CardTitle>
 </CardHeader>
 <CardContent>
 {blog.likes.length > 0 ? (
 <div className="flex flex-wrap gap-2">
 {blog.likes.map((like) => (
 <Badge key={like.id} variant="outline">
 <Mail className="w-3 h-3 mr-1" />
 {like.user.applierProfile?.fullName || like.user.email}
 </Badge>
 ))}
 </div>
 ) : (
 <p className="text-muted-foreground text-sm text-center py-8">No likes yet.</p>
 )}
 </CardContent>
 </Card>

 {/* Comments */}
 <Card>
 <CardHeader>
 <CardTitle className="flex items-center gap-2">
 <MessageCircle className="w-5 h-5 text-blue-500" /> 
 Recent Comments
 </CardTitle>
 </CardHeader>
 <CardContent>
 {blog.comments.length > 0 ? (
 <div className="space-y-4">
 {blog.comments.map((comment) => (
 <Card key={comment.id} className="hover:shadow-sm transition-shadow">
 <CardContent className="p-4">
 <div className="flex justify-between items-start mb-2">
 <div>
 <span className="font-semibold text-sm">
 {comment.user.applierProfile?.fullName || comment.user.email}
 </span>
 <span className="text-xs text-muted-foreground ml-2">
 {format(new Date(comment.createdAt), "MMM d, h:mm a")}
 </span>
 </div>
 <ConfirmDialog
 trigger={
 <Button 
 variant="ghost" 
 size="sm"
 className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
 >
 <Trash2 className="w-4 h-4" />
 </Button>
 }
 title="Delete Comment"
 description="Are you sure you want to delete this comment? This action cannot be undone."
 onConfirm={() => handleDelete(comment.id)}
 confirmText="Delete"
 variant="destructive"
 />
 </div>
 <p className="text-sm leading-relaxed border-l-2 border-primary pl-4 italic">
 "{comment.content}"
 </p>
 </CardContent>
 </Card>
 ))}
 </div>
 ) : (
 <p className="text-muted-foreground text-sm text-center py-12">No comments yet.</p>
 )}
 </CardContent>
 </Card>
 </div>
 </div>
 </div>
 </div>
 );
}
